import React from "react";

import styled from "@mui/material/styles/styled";
import Typography, { TypographyProps } from "@mui/material/Typography";

import TableCell, {
  TableCellProps,
} from "@app/analytics/components/Table/TableCell";
import TableRow, {
  TableRowProps,
} from "@app/analytics/components/Table/TableRow";

import Stack from "@rui/Stack";

interface ReportTableRowProps extends TableRowProps {
  expanded?: boolean;
  nested?: boolean;
  unseen?: boolean;
}

export const ReportTableRow = styled<React.FC<ReportTableRowProps>>(
  ({ expanded: _e, nested: _n, unseen: _u, ...props }) => (
    <TableRow {...props} />
  )
)(({ expanded = false, nested = false, unseen = false, onClick, theme }) => ({
  cursor: onClick ? "pointer" : "default",
  backgroundColor: expanded
    ? theme.palette.shades.secondary[100]
    : unseen
      ? theme.palette.shades.ghost[300]
      : "#FFF",
  ...(unseen && {
    "& .report-table-row__title": {
      color: theme.palette.shades.primary[500],
      fontWeight: 500,
    },
  }),
  "&.MuiTableRow-root .MuiTableCell-root": {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    borderBottomColor: expanded
      ? theme.palette.shades.secondary[100]
      : undefined,
  },
  "&:hover": {
    backgroundColor: onClick
      ? expanded
        ? theme.palette.shades.secondary[100]
        : theme.palette.shades.ghost[300]
      : undefined,
  },
  transition: "background 0.1s ease-out",
  position: "relative",
  "&::after": {
    width: "3px",
    content: "''",
    position: "absolute",
    top: -1,
    left: 0,
    bottom: 0,
    backgroundColor:
      expanded || nested ? theme.palette.shades.primary[500] : "transparent",
  },
  "&.report-table-row--nested:has(~ .report-table-row--nested)": {
    "& .MuiTableCell-root": {
      borderColor: theme.palette.shades.neutral[50],
    },
  },
}));

export const ProcessingTableRow = styled(TableRow)(({ theme, onClick }) => ({
  "&.MuiTableRow-root": {
    width: "100%",
    height: "69px",
  },
  boxSizing: "border-box",
  "&.MuiTableRow-root .MuiTableCell-root": {
    padding: 0,
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    overflow: "hidden",
    "& > div": {
      border: "1px dashed",
      borderColor: theme.palette.shades.neutral[150],
      borderLeft: 0,
      borderRight: 0,
    },
    "&:first-of-type": {
      "& > div": {
        borderLeft: "1px dashed",
        borderColor: theme.palette.shades.neutral[150],
        borderTopLeftRadius: "4px",
        borderBottomLeftRadius: "4px",
      },
      paddingLeft: theme.spacing(0.5),
      "& .processing__inner": {
        paddingLeft: theme.spacing(1.5),
      },
    },
    "&:last-child": {
      "& > div": {
        borderRight: "1px dashed",
        borderColor: theme.palette.shades.neutral[150],
        borderTopRightRadius: "4px",
        borderBottomRightRadius: "4px",
      },
    },
  },
  ...(onClick && {
    "&:hover": {
      cursor: "pointer",
    },
  }),
}));

export const ProcessingCellText = styled<React.FC<TypographyProps>>((props) => (
  <Typography variant="bodySecondaryReg" noWrap {...props} />
))(({ theme }) => ({
  color: theme.palette.shades.neutral[600],
}));

export const ProcessingCell = styled<React.FC<TableCellProps>>(
  ({ children, sx, ...props }) => (
    <TableCell {...props}>
      <Stack className="processing__outer" sx={sx}>
        <Stack className="processing__inner" spacing={0.5}>
          {children}
        </Stack>
      </Stack>
    </TableCell>
  )
)(({ theme }) => ({
  "& .processing__outer": {
    height: "100%",
    backgroundColor: theme.palette.background.default,
  },
  "& .processing__inner": {
    justifyContent: "center",
    paddingLeft: theme.spacing(2),
    height: "100%",
    width: "100%",
    maxWidth: "100%",
  },
}));
